import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Lightbulb, X } from "lucide-react";

export function ProjectRenameDialog({
  isOpen,
  onOpenChange,
  workspaceToRename,
  newWorkspaceName,
  setNewWorkspaceName,
  isRenamingWorkspace,
  onRename,
  onCancel,
}) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && newWorkspaceName.trim()) {
      e.preventDefault();
      onRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] w-[500px] bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 rounded-2xl"
        style={{ maxHeight: "min(90vh, 400px)", zIndex: 9999 }}
        hideCloseButton={true}
      >
        <DialogHeader className="relative">
          <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
            Rename Project
          </DialogTitle>
          <DialogDescription className="sr-only">
            Enter a new name for this project
          </DialogDescription>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            className="absolute right-0 top-0 h-8 w-8 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Project Name Input */}
          <div className="space-y-2">
            <Label
              htmlFor="project-name"
              className="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Project Name
            </Label>
            <Input
              id="project-name"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder="Enter project name"
              className="h-10 border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800"
              autoFocus
              onKeyDown={handleKeyDown}
              disabled={isRenamingWorkspace}
            />
          </div>

          {/* Helper Text */}
          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-zinc-800/50 rounded-lg">
            <Lightbulb className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Renaming your project
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                This will update the project name across all chats and shared
                links.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isRenamingWorkspace}
          >
            Cancel
          </Button>
          <Button
            onClick={onRename}
            disabled={isRenamingWorkspace || !newWorkspaceName.trim()}
            className="bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 rounded-2xl"
          >
            {isRenamingWorkspace ? (
              <>
                <Spinner className="h-4 w-4 mr-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
